import { Controller } from '@hotwired/stimulus';
import Papa from 'papaparse';
import { Module, TabulatorFull as Tabulator } from 'tabulator-tables';
import { showMessage, confirmMessage } from './message_controller';
import { openImage } from './modal_controller';

function getCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]').getAttribute('content');
}

// subject_origin values that view_record_rbf can render; only these get a subject "View" link
const SUBJECT_LINK_ORIGINS = ['installations', 'shs', 'meters', 'payments_ts'];

// Maps two different sets of columns to each other
// used for RBF forms, that have different attribute names
class Mapper {
  map;
  reverse_map;

  // initializes the mapping for both directions
  constructor(mapping, category) {
    if (mapping?.[category]) {
      this.map = mapping[category];
      this.reverse_map = this.flip(mapping[category]);
    }
  }

  // creates a flipped mapping from the given one
  // only used in the initialization
  flip(mapping) {
    return Object.fromEntries(
      Object.entries(mapping).map(([key, value]) => {
        const v = structuredClone(value);
        const k = value.field;
        v.field = key;
        return [k, v];
      })
    );
  }

  // fetch field info from the mapping via a key
  key(value) {
    return this.getFieldInfo(value, this.map);
  }

  // fetch field info from the reverse mapping via a key
  reverseKey(value) {
    return this.getFieldInfo(value, this.reverse_map);
  }

  // find our field with some fuzzy logic, if not found, return the original value
  // if we have a map but can not find the value, we assume it is s field that changed it's name
  // after we opened the vault and removed the postfix
  getFieldInfo(value, map) {
    if (map) {
      if (map[value]) {
        return map[value];
      }
      if (map[value.slice(0, -2)]) {
        const tmp = structuredClone(map[value.slice(0, -2)]);
        tmp.field = `${tmp.field}_e`;
        return tmp;
      }
    } else {
      return { field: value };
    }
  }
}

// implement filter with ransacker names
// list not exhausted
Tabulator.extendModule('filter', 'filters', {
  eq: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal === rowVal;
  },
  lt: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal < rowVal;
  },
  gt: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal > rowVal;
  },
  lteq: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal <= rowVal;
  },
  gteq: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal >= rowVal;
  },
  not_eq: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal !== rowVal;
  },
  matches: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal.matches(rowVal);
  },
});

// custom Filter module that implements a really crude interface to add filters to the table
// the idea was to have remote and local filters
// remote filters can be done by the database
// and local filter can only be applied in the browser
// because the fields are encrypted on the server
class Filter {
  column_select;
  filter_select;
  filter_input;
  filter_count;
  filter_list;
  table;
  remote_filter;
  local_filter;

  constructor(
    table,
    column_select,
    filter_select,
    filter_input,
    filter_count,
    filter_list,
    add_filter_button,
    clear_filter_button,
    columns
  ) {
    // if there are already filters, use them, the filter is recreated
    // after the vault was opened and column names changed in the process
    if (table.filter) {
      this.remote_filter = table.filter.remote_filter;
      this.local_filter = table.filter.local_filter;
    } else {
      this.remote_filter = [];
      this.local_filter = [];
    }

    this.table = table;

    column_select.innerHTML = '';

    // we add each column to the available columns list
    for (const c of columns) {
      // there could be an "action" column for specific rbf data tables, that contains no data
      // just buttons which we want to skip
      if (this.table.options?.rbf && c === 'actions') {
        continue;
      }

      const option = new Option();

      // if we have a mapping, we want to filter by the mapped fields
      const mapping = this.table.options.mapper.reverseKey(c);

      if (mapping) {
        option.value = mapping.field;
        option.encrypted = mapping.encrypted;
      }
      option.text = c;

      // if the column is encrypted and the vault is not open,
      // we disable filtering for this column
      if (c.endsWith('_e')) {
        option.disabled = true;
      } else {
        // if the vault is open, but the column is encrypted, we only allow local filtering
        if (option.encrypted) {
          option.text = `${option.text} (local)`;
        } else {
          option.text = `${option.text} (remote)`;
        }
      }

      column_select.add(option);
    }

    // mapping to ransacker filters from "mathy" symbols
    const filter = [
      { text: '=', value: 'eq' },
      { text: '<', value: 'lt' },
      { text: '>', value: 'gt' },
      { text: '<=', value: 'lteq' },
      { text: '>=', value: 'gteq' },
      { text: '!=', value: 'not_eq' },
      { text: '~', value: 'matches' },
    ];

    // wipe whatever was in the filter
    filter_select.innerHTML = '';

    // add all options
    for (const f of filter) {
      filter_select.add(new Option(f.text, f.value));
    }

    // store all our elements for later use
    this.column_select = column_select;
    this.filter_select = filter_select;
    this.filter_input = filter_input;
    this.filter_count = filter_count;
    this.filter_list = filter_list;
    this.filter_count.parentElement.onclick = () => {
      this.filter_list.classList.toggle('hidden');
      this.column_select.classList.toggle('hidden');
      this.filter_select.classList.toggle('hidden');
      this.filter_input.classList.toggle('hidden');
      add_filter_button.classList.toggle('hidden');
      clear_filter_button.classList.toggle('hidden');
    };
    add_filter_button.onclick = this.addFilter.bind(this);
    clear_filter_button.onclick = this.clearFilters.bind(this);
  }

  // remove all applied filters
  clearFilters() {
    this.local_filter = [];
    this.remote_filter = [];
    this.table.options.filterMode = 'remote';
    this.table.clearFilter();
    this.updateFilterCount();
    this.updateFilterList();
  }

  // take the currently selected filter from the UI and apply it
  // if the column is encrypted it is added to the local filters
  addFilter() {
    const filter = `${this.column_select.value} ${this.filter_select.value} ${this.filter_input.value}`;
    if (this.column_select.selectedOptions[0].encrypted) {
      this.local_filter.push({
        field: this.table.options.mapper.key(this.column_select.value).field,
        type: this.filter_select.value,
        value: this.filter_input.value,
      });
    } else {
      this.remote_filter.push({
        field: this.column_select.value,
        type: this.filter_select.value,
        value: this.filter_input.value,
      });
    }
    this.triggerFilter();
  }

  // new filter method that used the adaptive filters
  addFilterNew(filterData) {
    this.remote_filter.push({
      field: filterData.column,
      type: filterData.function.replace('_', ''),
      value: `${filterData.selectValue}${filterData.inputValue}`,
    });
    // const filter = `${this.column_select.value} ${this.filter_select.value} ${this.filter_input.value}`;
    // if (this.column_select.selectedOptions[0].encrypted) {
    //   this.local_filter.push({
    //     field: this.table.options.mapper.key(this.column_select.value).field,
    //     type: this.filter_select.value,
    //     value: this.filter_input.value,
    //   });
    // } else {
    //   this.remote_filter.push({
    //     field: this.column_select.value,
    //     type: this.filter_select.value,
    //     value: this.filter_input.value,
    //   });
    // }
    this.triggerFilter();
  }

  // partition filters in remote and local filters based on the column type (encrypted/not encrypted)
  // disable columns for local filters if vault is locked, listen to unlock and unlock the filter
  triggerFilter() {
    // apply remote filters
    this.table.options.filterMode = 'remote';
    this.table.setFilter(this.remote_filter);

    // apply local filters
    this.table.options.filterMode = 'local';
    this.table.setFilter(this.local_filter);

    this.table.module('filter').filterList = this.remote_filter;

    this.filter_input.value = '';
    this.updateFilterCount();
    this.updateFilterList();
  }

  // updates the visual list of applied filter for the user
  updateFilterList() {
    // clean list
    this.filter_list.innerHTML = '';

    // function that adds the element and also a remove button for this specific filter
    const addElementToList = (index, filter, list) => {
      const div = document.createElement('div');
      div.classList.add('m-1', 'cursor-pointer');
      div.innerText = `${filter.field} ${filter.type} ${filter.value}`;
      this.filter_list.append(div);

      const remove = document.createElement('span');
      remove.innerText = '✖ ';
      remove.onclick = () => {
        list.splice(index, 1);
        this.triggerFilter();
      };
      div.prepend(remove);
    };

    // apply for remote filters
    for (const [index, filter] of this.remote_filter.entries()) {
      addElementToList(index, filter, this.remote_filter);
    }

    // apply for local filters
    for (const [index, filter] of this.local_filter.entries()) {
      addElementToList(index, filter, this.local_filter);
    }
  }

  // update visual counter of applied filters
  updateFilterCount() {
    const filter_count = this.remote_filter.length + this.local_filter.length;
    if (filter_count > 0) {
      this.filter_count.innerText = filter_count;
      this.filter_count.classList.remove('hidden');
    } else {
      this.filter_count.classList.add('hidden');
    }
  }
}

// this module does all the work of splitting the custom column into it's own columns
// add an optional action column at the front for RBF forms
// put encrypted images behind a separate load button
// set's the correct formatting for each column
// adds an editing mode to update specific values
class SelectableColumnsModule extends Module {
  static moduleName = 'selectableColumns';

  initialize() {
    // we add a custom handler for the ajax response
    this.table.options.ajaxResponse = async (...[, , response]) => {
      const final_data = [];
      for (const row of response.data) {
        // split off the custom column
        const { custom, ...data } = row;

        // stringify any value in the custom column that is a json otherwise it will just show: [Object object]
        if (custom && typeof custom === 'object') {
          for (const key of Object.keys(custom)) {
            if (typeof custom[key] === 'object' && custom[key] !== null) {
              custom[key] = JSON.stringify(custom[key]);
            }
          }
        }

        // will contain the flattened columns from custom columns
        let combined_row;
        // will contain new columns that come from added actions or decrypted columns
        let new_column;

        if (
          this.table.options?.rbf &&
          this.table.options.actions_column &&
          this.table.options.data_category
        ) {
          const params = new URLSearchParams(location.search);
          let rct_id = params.get('rbf_claim_template_id');
          let dc = this.table.options.data_category;
          console.log(dc);
          let frameId = `actions_${row.uid}`.replace(/\W/g, '_');
          let searchParamsObj = {
            frame: frameId,
            import_id: row.last_import_id || row.import_id,
            rbf_claim_template_id: rct_id,
            data_category: dc,
            uid: row.uid,
          };
          let searchParams = new URLSearchParams(searchParamsObj);

          const frame = document.createElement('turbo-frame');
          frame.id = frameId;
          frame.src = `/projects/view_data_rbf_actions?${searchParams.toString()}`;
          frame.loading = 'lazy';
          frame.textContent = 'Loading...';
          frame.classList.add('animate-pulse');

          combined_row = { actions: frame, ...data, ...(custom || {}) };
          new_column = { actions: frame };
        } else if (
          this.table.options?.rbf &&
          this.table.options.subject_link_column &&
          SUBJECT_LINK_ORIGINS.includes(row.subject_origin)
        ) {
          // trust_trace row -> link to its subject's record (subject_origin/subject_uid),
          // only for subject categories that view_record_rbf supports
          const rct_id = new URLSearchParams(location.search).get('rbf_claim_template_id');
          const frameId = `actions_${row.uid}`.replace(/\W/g, '_');
          const searchParams = new URLSearchParams({
            frame: frameId,
            import_id: row.last_import_id || row.import_id,
            rbf_claim_template_id: rct_id,
            data_category: row.subject_origin,
            uid: row.subject_uid,
            subject_link: true,
          });

          const frame = document.createElement('turbo-frame');
          frame.id = frameId;
          frame.src = `/projects/view_data_rbf_actions?${searchParams.toString()}`;
          frame.loading = 'lazy';
          frame.textContent = 'Loading...';
          frame.classList.add('animate-pulse');

          combined_row = { actions: frame, ...data, ...(custom || {}) };
          new_column = { actions: frame };
        } else {
          combined_row = { ...data, ...(custom || {}) };
          new_column = {};
        }

        // decrypt columns if the vault is open
        if (window.vault?.is_open()) {
          for (const key in combined_row) {
            if (key.endsWith('_e')) {
              // if we have an encrypted blob, we just copy the value
              // it can be viewed later via the custom image format
              if (combined_row[key]?.match('http.*encrypted_blob')) {
                new_column[key.slice(0, -2)] = combined_row[key];
              } else {
                new_column[key.slice(0, -2)] = await window.vault.decryptMessage(combined_row[key]);
              }
            } else {
              new_column[key] = combined_row[key];
            }
          }
          final_data.push(new_column);
        } else {
          final_data.push(combined_row);
        }
      }

      return {
        data: final_data,
        errors: response.errors,
        last_page: response.last_page,
      };
    };

    // subscribe to table events
    this.subscribe('table-initialized', this.addButtonAction.bind(this));
    this.subscribe('columns-loaded', this.initializeColumn.bind(this));
    this.subscribe('cell-value-changed', this.cellUpdated.bind(this));
    this.subscribe('data-loaded', this.mapData.bind(this));
    this.subscribe('column-format', this.formatColumn.bind(this));
    this.subscribe('data-loading', this.dataLoading.bind(this));
    if (this.table.downloadCsvButton)
      this.subscribe('data-refreshed', this.updateExportButton.bind(this));
  }

  updateExportButton() {
    // update the label with the approximate number of rows and, if a filter is
    // currently applied, swap in the "filtered" i18n label so it's clear the export
    // won't include the full unfiltered dataset. Both labels come from the server
    // (i18n) - only the row-count suffix is built in JS.
    const approxRows = this.table.getPageSize() * this.table.getPageMax();
    const filterCount =
      (this.table.filter?.remote_filter?.length || 0) +
      (this.table.filter?.local_filter?.length || 0);
    const baseLabel =
      filterCount > 0 ? this.table.downloadCsvFilteredLabel : this.table.downloadCsvBaseLabel;
    this.table.downloadCsvLabel.textContent = `${baseLabel} (~${approxRows})`;
  }

  // TODO: figure out why I did this
  dataLoading(data, params, config, silent) {
    if (this.table.filter) {
      this.table.options.filterMode = 'remote';
      this.table.module('filter').filterList = this.table.filter.remote_filter;
    }
    return true;
  }

  // add format to the columns
  formatColumn(column, title, el) {
    const mapping = this.table.options.mapper.reverseKey(title);
    // if the vault is not open, we ignore the encrypted columns
    if (window.vault && !window.vault.is_open() && mapping?.encrypted) {
      return title;
    }

    // the action column is "special", not sortable and
    // should be handled as html to trigger the script inside
    // else we just set the formatter from the mapping (from the RBF form)
    if (
      this.table.options?.rbf &&
      (this.table.options.actions_column || this.table.options.subject_link_column) &&
      title === 'actions'
    ) {
      column.definition.headerSort = false;
      column.definition.formatter = 'html';
    } else {
      column.definition.formatter = mapping?.format;
    }

    return title;
  }

  // use the mapper to map the data to the correct columns
  mapData(data) {
    // TODO: figure out why I did this
    // I might have done this to apply the filter because the code is run after this is executed
    if (this.table.filter) {
      this.table.options.filterMode = 'local';
      this.table.module('filter').filterList = this.table.filter.local_filter;
    }

    // if we have no mapper, return the data as is
    if (!this.table.options.mapper) {
      return data.data;
    }

    const mapped_data = [];

    for (const row of data.data) {
      const mapped_row = {};

      // actions stay actions
      if (
        this.table.options?.rbf &&
        (this.table.options.actions_column || this.table.options.subject_link_column)
      ) {
        mapped_row.actions = row.actions;
      }

      for (const [key, value] of Object.entries(row)) {
        // TODO: we need some kind of mechanism to map _e or _p or _b(?) specifically
        if (key.endsWith('_p')) {
          continue;
        }

        const mapped = this.table.options.mapper.key(key);
        if (mapped) {
          mapped_row[mapped.field] = value;
        }
      }
      mapped_data.push(mapped_row);
    }

    return mapped_data;
  }

  // change the background color of the cell to indicate it has been edited
  cellUpdated(cell) {
    cell.getElement().style['background-color'] = 'rgb(93, 239, 236)';
    cell.edited = true;
  }

  addButtonAction() {
    if (this.table.options.update_path === null) {
      this.table.editButton.classList.toggle('hidden');
    }
    this.table.editButton.onclick = () => {
      this.editMode();
    };
    this.table.cancelButton.onclick = () => {
      this.readMode();
    };
    this.table.commitButton.onclick = () => {
      this.saveChanges();
    };
  }

  // creates a csv with the whole row with any edited cell and
  // sends it to the server to get ingested
  saveChanges() {
    const updated_rows = this.table
      .getRows()
      .filter((row) => row.getCells().filter((cell) => cell._cell.edited).length > 0);
    const data = updated_rows.map((row) => row.getData());

    if (data.length === 0) {
      showMessage('No changes found');
      return;
    }

    const csv = Papa.unparse(data);

    const fd = new FormData();
    fd.append('source[data_category]', this.table.options.data_category);
    fd.append('source[file]', new Blob([csv]), 'update.csv');
    fd.append('replace', 'true'); // edited records: the server only logs dropped columns like <field>_e

    fetch(this.table.options.update_path, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': getCsrfToken(),
      },
      body: fd,
    })
      .then((response) => {
        if (response.ok) {
          window.location = response.url;
        } else {
          showMessage('Error occurred while submitting changes.');
        }
      })
      .catch((error) => {
        showMessage('Error occurred while submitting changes.');
        console.log('Submission error:', error);
      });
  }

  // switch to edit mode
  async editMode() {
    // warn the user, that edit mode without an open vault has its caveats
    if (window.vault && !window.vault.is_open()) {
      const confirmed = await confirmMessage(
        'Vault is locked, encrypted data not viewable and can only be completely overwritten.'
      );
      if (!confirmed) return;
    }

    const columns = this.table.getColumns();
    const data_table = this.table.options.data_table;

    // not all columns are updateable, we indicate locked columns with a lock icon and grey text
    for (const column of columns) {
      if (
        data_table[column.getDefinition().title]?.exclude_from_ingress ||
        data_table[this.table.options.mapper.reverseKey(column.getDefinition().title)?.field]
          ?.exclude_from_ingress
      ) {
        column.getDefinition().title = `${column.getDefinition().title} 🔒`;
        column.getDefinition().formatter = (...[cell, ,]) => {
          cell.getElement().classList.add('text-grey-4');
          return cell.getValue();
        };
      } else {
        column.getDefinition().editor = 'input';
      }
    }

    this.table.setColumns(columns.map((c) => c.getDefinition()));
    // display commit and cancel buttons
    this.table.editButton.classList.add('hidden');
    this.table.commitButton.classList.remove('hidden');
    if (this.table.editDescription) {
      this.table.editDescription.classList.remove('hidden');
    }
    this.table.cancelButton.classList.remove('hidden');
  }

  // switch to read mode
  readMode() {
    const columns = this.table.getColumns();
    const data_table = this.table.options.data_table;

    for (const column of columns) {
      // restore old values which were not committed
      for (const cell of column.getCells()) {
        if (cell._cell.edited) {
          cell.setValue(cell.getOldValue());
        }
      }
      if (data_table[column.getDefinition().field]?.exclude_from_ingress) {
        // remove the lock symbol from the title and revert the text color changes
        column.getDefinition().title = [...column.getDefinition().title].slice(0, -2).join('');
        column.getDefinition().formatter = (...[cell, ,]) => {
          cell.getElement().classList.remove('text-grey-4');
          return cell.getValue();
        };
      } else {
        column.getDefinition().editor = undefined;
      }
    }

    this.table.setColumns(columns.map((c) => c.getDefinition()));
    this.table.editButton.classList.remove('hidden');
    this.table.cancelButton.classList.add('hidden');
    if (this.table.editDescription) {
      this.table.editDescription.classList.add('hidden');
    }
    this.table.commitButton.classList.add('hidden');
  }

  // initialize column selection dropdown and the column filter
  initializeColumn() {
    // if there is no data, we have no columns, the end
    if (this.table.getColumns().length === 0) {
      return;
    }

    const active_selection = {};

    // check if we already have an active selection and use it if
    // the columns change for example after opening the vault
    if (this.table.column_filter) {
      for (const option of this.table.column_filter.options) {
        active_selection[option.innerText] = option.selected;
      }

      // remove the old filter dropdown
      if (this.table.column_filter.parentElement) {
        this.table.column_filter.parentElement.removeChild(this.table.column_filter);
      }
    }

    const column_select = document.createElement('select');
    column_select.multiple = true;

    for (const column of this.table.getColumns()) {
      if (this.table.options?.rbf && column.getDefinition().field === 'actions') {
        continue;
      }
      // we try to be faster than the onclick event if the user clicks on the column header,
      // that triggers the sort, to change the sort mode based on the data in the column
      // I found no better way to do this...
      const mapped = this.table.options.mapper.reverseKey(column.getDefinition().field);
      column.getElement().onmousedown = (_e) => {
        if (mapped.encrypted || column.getDefinition().field.endsWith('_e')) {
          this.table.options.sortMode = 'local';
          this.table.module('filter').filterList = this.table.filter.local_filter;
        } else {
          this.table.options.sortMode = 'remote';
        }
      };
      // encrypted columns are too big with the 'fitData' layout
      // there seems to be no option to set this globally, so
      // we do it here on each column, makes the table look way better
      if (
        column._column.field.endsWith('_p') ||
        column._column.field.endsWith('_e') ||
        column._column.field.includes('photo')
      ) {
        column._column.setMaxWidth(200);
      }

      // add all columns to the select element
      const option = new Option();
      option.innerText = column.getDefinition().field;

      if (option.innerText in active_selection) {
        option.selected = active_selection[option.innerText];
      } else {
        option.selected = true;
      }

      option.column = column;
      column_select.add(option);
    }

    // trigger the visibility change event if a column is selected or deselected
    column_select.onchange = () => {
      for (const option of column_select.options) {
        if (option.selected ^ option.column.isVisible()) {
          option.column.toggle();
          this.table.redraw();
        }
      }
    };

    this.table.control_element.after(column_select);
    this.table.column_filter = column_select;

    if (this.table.tom) {
      this.table.tom.destroy();
    }

    this.table.tom = new TomSelect(column_select, {
      plugins: {
        checkbox_options: {
          checkedClassNames: ['ts-checked'],
          uncheckedClassNames: ['ts-unchecked'],
        },
        remove_button: {
          title: "Don't show this column",
        },
      },
    });

    column_select.onchange();
    this.table.tom.control.classList.add('!hidden');

    this.table.headerIcon.onclick = () => {
      this.table.tom.control.click();
    };

    this.table.filter = new Filter(
      this.table,
      this.table.columnSelect,
      this.table.filterSelect,
      this.table.filterInput,
      this.table.filterCount,
      this.table.filterList,
      this.table.addFilterButton,
      this.table.clearFilterButton,
      this.table.getColumns().map((c) => {
        return c.getField();
      })
    );

    // Deep-link filters (e.g. from an import page) arrive as tabulator filters via options.
    // Seed remote_filter so add/clear stay consistent, and apply them to the data. The visible
    // filter chips are rendered by the adaptive-filter controller, so we deliberately don't
    // touch the legacy funnel filter UI (updateFilterList/updateFilterCount) here.
    const initialFilters = this.table.options.initial_filters;
    if (initialFilters?.length && this.table.filter.remote_filter.length === 0) {
      this.table.filter.remote_filter.push(...initialFilters);
      this.table.options.filterMode = 'remote';
      this.table.setFilter(this.table.filter.remote_filter);
    }
  }
}

// ########### URL Generator ###########
// we overwrite the default url generator to build correct array parameters for rails
function generateParamsList(data, prefix) {
  let output = [];

  prefix = prefix || '';

  if (Array.isArray(data)) {
    data.forEach((item, i) => {
      output = output.concat(generateParamsList(item, prefix ? `${prefix}[]` : i));
    });
  } else if (typeof data === 'object') {
    for (const key in data) {
      output = output.concat(generateParamsList(data[key], prefix ? `${prefix}[${key}]` : key));
    }
  } else {
    output.push({ key: prefix, value: data });
  }

  return output;
}

function serializeParams(params) {
  const output = generateParamsList(params);
  const encoded = [];

  for (const item of output) {
    encoded.push(`${encodeURIComponent(item.key)}=${encodeURIComponent(item.value)}`);
  }

  return encoded.join('&');
}

// transform the tabulator sort into ransack sort
function transformSort(params) {
  const sort = structuredClone(params.sort);
  delete params.sort;
  params.s = [];
  for (const obj of sort) {
    const mapping = this.options.mapper.reverseKey(obj.field);
    params.s.push(`${mapping.field} ${obj.dir}`);
  }

  return params;
}

function generateUrl(url, config, params) {
  if (url) {
    if (params && Object.keys(params).length) {
      if (params.sort) {
        params = transformSort.bind(this)(params);
      }
      if (!config.method || config.method.toLowerCase() === 'get') {
        config.method = 'get';

        url += (url.includes('?') ? '&' : '?') + serializeParams(params);
      }
    }
  }

  return url;
}

// ########### URL Generator ###########

// improve image formatter to ignore undefined values
// also images will be hidden behind a button,
// which triggers the fetch and decryption
Tabulator.extendModule('format', 'formatters', {
  image: (cell, formatterParams, onRendered) => {
    const el = document.createElement('button');
    el.style = 'border: 1px solid;padding: 2px;border-radius: 5px;';
    el.innerText = 'Load Image';

    el.onclick = async (event) => {
      // create button to load image
      let src = cell.getValue();

      if (src === undefined) {
        console.log('Undefined Source!');
        event.target.replaceWith(document.createElement('div'));
        return;
      }

      src = await window.vault.decryptMessage(await (await fetch(src)).text());

      if (src === null) {
        const replacementDiv = document.createElement('div');
        replacementDiv.textContent = 'NO DATA';
        event.target.replaceWith(replacementDiv);
        return;
      }

      if (formatterParams.urlPrefix) {
        src = formatterParams.urlPrefix + src;
      }

      if (formatterParams.urlSuffix) {
        src = src + formatterParams.urlSuffix;
      }

      let el = null;
      // some of our "images" are actually PDFs
      if (src.startsWith('data:application/pdf;')) {
        el = document.createElement('a');
        el.setAttribute('href', src);
        el.setAttribute('target', '_blank');
        el.classList.add('text-primary', 'hover:underline');
        el.innerText = 'Open PDF';
      } else {
        el = document.createElement('img');
        el.setAttribute('src', src);

        el.onclick = (e) => openImage(e.target.cloneNode());
      }

      switch (typeof formatterParams.height) {
        case 'number':
          el.style.height = `${formatterParams.height}px`;
          break;

        case 'string':
          el.style.height = formatterParams.height;
          break;
      }

      switch (typeof formatterParams.width) {
        case 'number':
          el.style.width = `${formatterParams.width}px`;
          break;

        case 'string':
          el.style.width = formatterParams.width;
          break;
      }

      el.addEventListener('load', () => {
        cell.getRow().normalizeHeight();
      });

      event.target.replaceWith(el);
    };

    return el;
  },
});

export default class extends Controller {
  static targets = [
    'editDescription',
    'filterSelect',
    'table',
    'control',
    'editButton',
    'cancelButton',
    'commitButton',
    'headerIcon',
    'columnSelect',
    'filterInput',
    'filterCount',
    'filterList',
    'clearFilter',
    'addFilter',
    'downloadCsv',
    'imageToggle',
  ];

  static values = {
    options: Object,
    url: String,
    exportSuccessMessage: String,
    exportFailedMessage: String,
    exportErrorMessage: String,
    exportFilteredDataLabel: String,
  };

  connect() {
    Tabulator.registerModule(SelectableColumnsModule);

    const optionsJson = this.element.dataset.tabulatorOptions;
    if (optionsJson && this.hasTableTarget) {
      const options = JSON.parse(optionsJson);
      options.mapper = new Mapper(options.mapper, options.data_category);

      this.table = new Tabulator(this.tableTarget, options);
      this.table.options.ajaxURLGenerator = generateUrl.bind(this.table);

      // TODO: maybe bind the controller to the table and access the targets directly
      this.table.editDescription = this.hasEditDescriptionTarget
        ? this.editDescriptionTarget
        : null;
      this.table.control_element = this.controlTarget;
      this.table.editButton = this.editButtonTarget;
      this.table.cancelButton = this.cancelButtonTarget;
      this.table.commitButton = this.commitButtonTarget;
      this.table.headerIcon = this.headerIconTarget;
      this.table.columnSelect = this.columnSelectTarget;
      this.table.filterSelect = this.filterSelectTarget;
      this.table.filterInput = this.filterInputTarget;
      this.table.filterCount = this.filterCountTarget;
      this.table.filterList = this.filterListTarget;
      this.table.clearFilterButton = this.clearFilterTarget;
      this.table.addFilterButton = this.addFilterTarget;

      // Set up CSV download button
      if (this.hasDownloadCsvTarget) {
        this.table.downloadCsvButton = this.downloadCsvTarget;
        // the label is the innermost span rendered by ButtonComponent, next to the icon <i>;
        // we only ever touch this span's text so the icon and the server-rendered (i18n) label survive refreshes
        this.table.downloadCsvLabel =
          this.downloadCsvTarget.querySelector('span > span') || this.downloadCsvTarget;
        this.table.downloadCsvBaseLabel = this.table.downloadCsvLabel.textContent;
        this.table.downloadCsvFilteredLabel = this.exportFilteredDataLabelValue;
        this.downloadCsvTarget.onclick = this.downloadCsv.bind(this);
      }

      window.myTabulatorInstance = this.table;
    }
  }

  async downloadCsv() {
    const dataCategory = this.table.options.data_category;
    const customView = this.table.options.custom_view;
    const projectId = this.getProjectIdFromUrl();

    if (!dataCategory || !projectId) {
      console.error('Missing data category or project ID for CSV download');
      return;
    }

    // Get current filters and create a form for turbo-confirm
    const currentFilters = this.getCurrentFilters();
    await this.createExport(projectId, dataCategory, currentFilters, customView);
  }

  async createExport(projectId, dataCategory, currentFilters, customView) {
    try {
      // Get total record count (not paginated)
      const response = await fetch('/exports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          project_id: projectId,
          data_category: dataCategory,
          filter: JSON.stringify(currentFilters),
          custom_view: customView,
        }),
      });

      if (response.ok) {
        console.log('Export queued successfully');
        showMessage(this.exportSuccessMessageValue);
      } else {
        console.error('Failed to create export:', response.status);
        showMessage(this.exportFailedMessageValue);
      }
    } catch (error) {
      console.error('Error getting record count:', error);
      showMessage(this.exportErrorMessageValue);
    }
  }

  getProjectIdFromUrl() {
    const pathParts = window.location.pathname.split('/');
    const projectIndex = pathParts.indexOf('projects');
    return projectIndex !== -1 && pathParts[projectIndex + 1] ? pathParts[projectIndex + 1] : null;
  }

  getCurrentFilters() {
    // Get current filters from the tabulator
    const filters = [];

    // Get remote filters (applied to the API)
    if (this.table.filter?.remote_filter) {
      filters.push(...this.table.filter.remote_filter);
    }

    // Get local filters (applied to the data after loading)
    if (this.table.filter?.local_filter) {
      filters.push(...this.table.filter.local_filter);
    }

    // Get current sort order
    const sorters = this.table.getSorters().map((col) => `${col.field} ${col.dir}`);

    // Get current page size (for pagination)
    const pageSize = this.table.options.paginationSize || 25;

    return {
      filter: filters,
      s: sorters,
      pageSize: pageSize,
    };
  }
}
